<template>
  <div class="mindset-evaluation-container">
    <div class="mindset-sidebar">
      <div
        v-for="student in students"
        :key="student.id"
        class="student-item"
        :class="{ active: selectedStudent && selectedStudent.id === student.id }"
        @click="selectStudent(student)"
      >
        <div class="student-avatar">
          {{ getInitials(student.name) }}
        </div>
        <span class="student-name">{{ student.name }}</span>
        <i v-if="student.evaluated" class="fa fa-check-circle evaluated-icon"></i>
      </div>
    </div>
    
    <div class="mindset-main">
      <div class="mindset-header">
        <button class="back-btn" @click="$emit('close')"><i class="fa fa-arrow-left"></i></button>
        <div>
          <h2 class="header-title">Mindset Evaluation</h2>
          <p class="header-subtitle">Question Performance Overview & Student Engagement Metrics</p>
        </div>
      </div>
      
      <div v-if="loading" class="mindset-loading-state">
        <i class="fa fa-spinner fa-spin fa-3x" style="color: #3b82f6; margin-bottom: 12px;"></i>
        <p style="margin: 0; font-size: 14px; color: #94a3b8;">Fetching mindset mappings...</p>
      </div>
      
      <div v-else-if="selectedStudent" class="mindset-content">
        <div class="card-section">
          <h3 class="section-title">Select {{ selectedStudent.firstName }}'s Mindset</h3>
          <p class="section-subtitle">Choose a mindset that best you want to highlight this week for this student</p>
          
          <div class="mindset-options">
            <label v-for="mindset in mindsets" :key="mindset.id" class="mindset-radio">
              <input type="radio" :value="mindset.id" v-model="selectedMindsetId" @change="handleMindsetChange" />
              <div class="radio-custom"></div>
              <div class="mindset-label">
                <div class="mindset-name">{{ mindset.name }}</div>
                <div class="mindset-score" :style="{ color: getScoreColor(mindset.score) }">{{ mindset.score }}/100</div>
              </div>
            </label>
          </div>
        </div>
        
        <div class="card-section" style="margin-top: 24px;">
          <h3 class="section-title">Observed Strengths in Class</h3>
          <p class="section-subtitle">Which behaviours did you observe that contributed to {{ selectedStudent.firstName }}'s high {{ selectedMindsetName }} score?</p>
          
          <div class="strengths-list">
            <label v-for="strength in currentStrengths" :key="strength.id" class="strength-checkbox">
              <input type="checkbox" :value="strength.id" v-model="selectedStrengths" />
              <div class="checkbox-custom"><i class="fa fa-check"></i></div>
              <span class="strength-label">{{ strength.text }}</span>
            </label>
          </div>
        </div>
        
        <div class="card-section" style="margin-top: 24px; border-bottom: none;">
          <h3 class="section-title">Additional Observations (Optional)</h3>
          <p class="section-subtitle">Share any specific moments or examples that stood out.</p>
          
          <div class="textarea-container">
            <textarea v-model="additionalObservations" placeholder="Type here..." rows="4"></textarea>
            <button class="mic-btn"><i class="fa fa-microphone"></i></button>
          </div>
        </div>
        
        <button class="save-btn" :disabled="!isFormValid || !hasChanges || isSaving" @click="saveDetails">
          <i v-if="isSaving" class="fa fa-spinner fa-spin" style="margin-right: 8px;"></i>
          {{ isSaving ? 'Saving...' : 'Save Details' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Dummy API
const mockFetchStudents = () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        { id: '1', name: 'Alex Santiago', evaluated: false },
        { id: '2', name: 'Beatrix Vane', evaluated: true },
        { id: '3', name: 'Caleb O\'Donnell', evaluated: true },
        { id: '4', name: 'Dina Moratti', evaluated: false },
        { id: '5', name: 'Elena Kovic', evaluated: true },
        { id: '6', name: 'Dina Moratti', evaluated: false }
      ])
    }, 500)
  })
}

const mockFetchMindsetData = (studentId) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        mindsets: [
          { id: 'm1', name: 'Interest', score: 92 },
          { id: 'm2', name: 'Confidence', score: 88 },
          { id: 'm3', name: 'Resilience', score: 61 },
          { id: 'm4', name: 'Growth Mindset', score: 54 },
          { id: 'm5', name: 'Relevance', score: 54 }
        ],
        strengths: {
          'm1': [
            { id: 's1', text: 'Asked thoughtful questions about the topic' },
            { id: 's2', text: 'Connected concepts to real-life examples' },
            { id: 's3', text: 'Showed curiosity and explored ideas deeply' },
            { id: 's4', text: 'Engaged in optional challenge questions' }
          ],
          'm2': [
            { id: 's5', text: 'Volunteered to answer difficult questions' },
            { id: 's6', text: 'Spoke clearly and confidently' }
          ]
        }
      })
    }, 300)
  })
}

const mockSaveDetails = (data) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true })
    }, 600)
  })
}

export default {
  name: 'MindsetEvaluation',
  props: {
    gameOnRecord: {
      type: Object,
      default: () => null
    },
    evaluations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      students: [],
      selectedStudent: null,
      mindsets: [],
      allStrengths: {},
      selectedMindsetId: '',
      selectedStrengths: [],
      additionalObservations: '',
      existingEvaluationId: null,
      isSaving: false,
      originalEvaluationData: null,
      loading: false
    }
  },
  computed: {
    selectedMindsetObj() {
      return this.mindsets.find(m => m.id === this.selectedMindsetId)
    },
    selectedMindsetName() {
      return this.selectedMindsetObj ? this.selectedMindsetObj.name : ''
    },
    selectedMindsetScore() {
      return this.selectedMindsetObj ? this.selectedMindsetObj.score : 0
    },
    currentStrengths() {
      return this.allStrengths[this.selectedMindsetId] || []
    },
    hasChanges() {
      if (!this.originalEvaluationData) return true;
      const currentData = JSON.stringify({
        mindsetId: this.selectedMindsetId,
        strengths: [...this.selectedStrengths].sort(),
        observations: this.additionalObservations
      });
      return currentData !== this.originalEvaluationData;
    },
    isFormValid() {
      return !!this.selectedMindsetId && this.selectedStrengths.length > 0;
    }
  },
  async created() {
    await this.loadStudents()
  },
  methods: {
    async loadStudents() {
      let record = this.gameOnRecord;
      
      // Fallback: fetch gameOnRecord from API if not provided via prop
      if (!record && window.App) {
        const sid = this.$store?.state?.session?.sid;
        if (sid) {
          try {
            const res = await window.App.service('game-on').find({
              query: { sid, $limit: 1, $sort: { createdAt: -1 } },
            });
            const records = res.data || res || [];
            if (records.length > 0) {
              record = records[0];
            }
          } catch(e) {
            console.error('Failed to fetch gameOnRecord', e);
          }
        }
      }

      let rawStudents = [];
      if (record) {
        const members = record.members || [];
        const guestMembers = record.guestMembers || [];
        rawStudents = [...members, ...guestMembers];
      }

      // Fallback to current classroom roster if gameOnRecord is completely empty
      if (rawStudents.length === 0 && this.$store?.state?.teacher?.rooms?.members) {
        rawStudents = this.$store.state.teacher.rooms.members;
      }
      
      this.students = rawStudents.map(member => {
        let name = 'Unknown Student';
        if (member.nickname) name = member.nickname;
        else if (member.name) {
          if (Array.isArray(member.name)) {
             name = member.name.filter(Boolean).join(' ');
          } else {
             name = member.name;
          }
        }
        
        return {
          id: member.id || member.uid || member._id || Math.random().toString(),
          name: name,
          evaluated: false
        };
      });

      // Fetch evaluation status
      if (this.students.length > 0 && this.evaluations && this.evaluations.length > 0) {
        const evaluatedIds = new Set(this.evaluations.map(e => String(e.studentId)));
        this.students.forEach(student => {
          if (evaluatedIds.has(String(student.id))) {
            student.evaluated = true;
          }
        });
      }

      // Fallback to dummy data only if absolutely no data is found
      if (this.students.length === 0) {
        this.students = await mockFetchStudents()
      }

      if (this.students.length > 0) {
        this.selectStudent(this.students[0])
      }
    },
    async selectStudent(student) {
      this.selectedStudent = {
        ...student,
        firstName: student.name.split(' ')[0]
      }
      this.selectedMindsetId = null
      this.selectedStrengths = []
      this.additionalObservations = ''
      this.existingEvaluationId = null
      
      this.loading = true;
      try {
        const res = await window.App.service('mindset-mapping').find({
          query: { $limit: 100 }
        });
        const mappings = res.data || res || [];
        
        this.mindsets = mappings.map(m => ({
          id: m._id,
          name: m.mindset,
          score: m.score
        }));
        
        const strengthsMap = {};
        mappings.forEach(m => {
          strengthsMap[m._id] = (m.strengths || []).map(s => ({
            id: s,
            text: s
          }));
        });
        this.allStrengths = strengthsMap;

        const existingEvaluation = this.evaluations && this.evaluations.find(e => String(e.studentId) === String(student.id));

        if (existingEvaluation) {
          this.existingEvaluationId = existingEvaluation._id;
          const mindsetObj = this.mindsets.find(m => m.name === existingEvaluation.mindset);
          if (mindsetObj) {
            this.selectedMindsetId = mindsetObj.id;
          } else if (this.mindsets.length > 0) {
            this.selectedMindsetId = this.mindsets[0].id;
          }
          this.selectedStrengths = existingEvaluation.strengths || [];
          this.additionalObservations = existingEvaluation.observations || '';
        } else {
          if (this.mindsets.length > 0) {
            this.selectedMindsetId = this.mindsets[0].id
          }
        }
        
        this.originalEvaluationData = JSON.stringify({
          mindsetId: this.selectedMindsetId,
          strengths: [...this.selectedStrengths].sort(),
          observations: this.additionalObservations
        });
      } catch (e) {
        console.error('Failed to fetch mindsets', e);
      } finally {
        this.loading = false;
      }
    },
    handleMindsetChange() {
      this.selectedStrengths = []
    },
    async saveDetails() {
      if (!this.isFormValid || !this.hasChanges || this.isSaving) return;
      this.isSaving = true;
      
      const record = this.gameOnRecord;
      const sid = (record && (record.sid)) || (this.$store && this.$store.state && this.$store.state.session && this.$store.state.session.sid);

      const payload = {
        studentId: this.selectedStudent.id,
        sid: sid,
        mindset: this.selectedMindsetName,
        baselineScore: this.selectedMindsetScore,
        strengths: this.selectedStrengths,
        observations: this.additionalObservations
      }
      console.log('Mindset Evaluation Payload to send to API:', payload)
      
      try {
        let savedRecord;
        if (this.existingEvaluationId) {
          savedRecord = await window.App.service('mindset-evaluation').patch(this.existingEvaluationId, payload)
        } else {
          savedRecord = await window.App.service('mindset-evaluation').create(payload)
          this.existingEvaluationId = savedRecord._id;
        }
        
        const student = this.students.find(s => s.id === this.selectedStudent.id)
        if (student) {
          student.evaluated = true
        }

        if (this.evaluations) {
          const idx = this.evaluations.findIndex(e => String(e.studentId) === String(payload.studentId));
          if (idx !== -1) {
            this.evaluations.splice(idx, 1, { ...this.evaluations[idx], ...payload });
          } else {
            this.evaluations.push(savedRecord);
          }
        }
        
        if (this.$message) {
          this.$message.success('Details saved successfully')
        } else if (window.$q) {
          window.$q.notify({ message: 'Details saved successfully', color: 'positive' })
        }
        
        this.originalEvaluationData = JSON.stringify({
          mindsetId: this.selectedMindsetId,
          strengths: [...this.selectedStrengths].sort(),
          observations: this.additionalObservations
        });
      } catch (e) {
        console.error('Failed to save evaluation', e);
        if (this.$message) {
          this.$message.error('Failed to save details')
        } else if (window.$q) {
          window.$q.notify({ message: 'Failed to save details', color: 'negative' })
        }
      } finally {
        this.isSaving = false;
      }
    },
    getInitials(name) {
      if (!name) return ''
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    },
    getAvatarBg(name) {
      const colors = ['#f1f5f9', '#e2e8f0', '#cbd5e1']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },
    getScoreColor(score) {
      if (score >= 80) return '#26a69a'
      return '#EA580C'
    }
  }
}
</script>

<style scoped>
.mindset-evaluation-container {
  display: flex;
  width: 100%;
  height: 100%;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  overflow: hidden;
}

.mindset-sidebar {
  width: 260px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

.student-item {
  display: flex;
  align-items: center;
  padding: 12px 16px 12px 20px;
  cursor: pointer;
  border-radius: 8px;
  gap: 12px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.student-item:hover:not(.active) {
  background: #f8fafc;
}

.student-item.active {
  background: #f0fdf4;
}

.student-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #10b981;
}

.student-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background-color: #e2e8f0;
  flex-shrink: 0;
}

.student-item.active .student-avatar {
  background-color: #10b981;
  color: #ffffff;
}

.student-name {
  font-size: 14px;
  color: #334155;
  font-weight: 500;
  flex: 1;
}

.student-item.active .student-name {
  color: #0f172a;
  font-weight: 600;
}

.evaluated-icon {
  color: #10b981;
  font-size: 16px;
}

.mindset-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 40px 60px;
}

.mindset-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 32px;
  width: 100%;
  max-width: 800px;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #1e293b;
  cursor: pointer;
  margin-right: 16px;
  margin-top: 4px;
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.header-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.mindset-content {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
}

.card-section {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px 0;
}

.mindset-options {
  display: flex;
  gap: 20px;
}

.mindset-radio {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
}

.mindset-radio input {
  display: none;
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  margin-right: 8px;
  margin-top: 2px;
  position: relative;
}

.mindset-radio input:checked + .radio-custom {
  border-color: #26a69a;
}

.mindset-radio input:checked + .radio-custom::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: #26a69a;
  border-radius: 50%;
}

.mindset-label {
  display: flex;
  flex-direction: column;
}

.mindset-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.mindset-score {
  font-size: 12px;
  font-weight: 600;
}

.strengths-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.strength-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.strength-checkbox input {
  display: none;
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.checkbox-custom i {
  color: white;
  font-size: 12px;
  display: none;
}

.strength-checkbox input:checked + .checkbox-custom {
  background: #26a69a;
  border-color: #26a69a;
}

.strength-checkbox input:checked + .checkbox-custom i {
  display: block;
}

.strength-label {
  font-size: 14px;
  color: #475569;
}

.textarea-container {
  position: relative;
}

textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 16px;
  font-size: 14px;
  color: #1e293b;
  resize: vertical;
  min-height: 100px;
  outline: none;
  box-sizing: border-box;
}

textarea:focus {
  border-color: #26a69a;
}

.mic-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: #26a69a;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.save-btn {
  width: 100%;
  background: #26a69a;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 32px;
}

.save-btn:hover:not(:disabled) {
  background: #1e8f85;
}

.save-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  opacity: 0.7;
}

.mindset-loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
}
</style>
